/**
 * The passkey answer to a sign-in's second factor (ECOSYSTEM-EMAIL-001 Q-EE-34 P5), and the ONE rule for what the MFA
 * screen says when an answer — a code or a passkey — is refused.
 *
 * The auth context fetches the ceremony's challenge for the live sign-in challenge and verifies the assertion
 * (`verifyMfaWithPasskey`); this module supplies the browser ceremony between the two, through the SAME assertion
 * helper the passwordless passkey sign-in uses, so a dismissed prompt is the same coded refusal on both.
 *
 * @module @puffin-core/login-shell/utils/mfaPasskeyAnswer
 */

import { WebAuthnService } from '@puffin-core/mfa-ui/webauthn';
import { getPasskeyAssertion } from './authenticateWithDiscoverablePasskey.js';
import { PASSKEY_LOGIN_ERROR_CODES } from './passkeyLoginErrors.js';

/**
 * A refused answer as this module reads it: whatever was thrown (an `Error`, a coded refusal), of which only these
 * fields are consulted.
 *
 * @typedef {Partial<Error> & {code?: string, messageKey?: string, terminal?: boolean}} AnswerRefusal
 */

/** The method name the sign-in events report for a passkey answer. */
export const MFA_PASSKEY_METHOD = 'passkey';

let sharedWebAuthnService = null;

/**
 * The page's WebAuthn helper — stateless, so one is shared; made on the first passkey answer, never at import (the
 * suites that double mfa-ui's barrel never reach it).
 *
 * @returns {WebAuthnService}
 */
function webAuthnService() {
  sharedWebAuthnService ??= new WebAuthnService();
  return sharedWebAuthnService;
}

/**
 * Answer the live challenge with a passkey: the context's verify, with the browser ceremony as its assertion.
 *
 * @param {{verifyMfaWithPasskey: Function}} auth - the surface's auth contract
 * @returns {Promise<Object>} the signed-in person (rejects as a refused code would)
 */
export function answerWithPasskey(auth) {
  return auth.verifyMfaWithPasskey((options) => getPasskeyAssertion(webAuthnService(), options));
}

/**
 * Whether a refusal is the person dismissing the passkey prompt — no server answered, so no translator words it.
 *
 * @param {?AnswerRefusal} err
 * @returns {boolean}
 */
export function isPasskeyPromptDismissed(err) {
  return err?.code === PASSKEY_LOGIN_ERROR_CODES.WEBAUTHN_CANCELLED;
}

/**
 * The passkey ceremony an in-session step-up runs (Q-EE-34 P5 unit 3), handed to mfa-ui's `createMfaStepUp` as
 * `passkeyCeremony`: the SAME browser half as the sign-in's answer, and the same reading of a closed prompt. mfa-ui
 * cannot import this package (this package depends on it), so the app hands it in.
 *
 * @type {Readonly<{getAssertion: (options: Object) => Promise<Object>, isDismissed: (err: unknown) => boolean}>}
 */
export const stepUpPasskeyCeremony = Object.freeze({
  getAssertion: (options) => getPasskeyAssertion(webAuthnService(), options),
  isDismissed: isPasskeyPromptDismissed,
});

/**
 * What the MFA screen holds for a refused answer: the catalog key and terminal tone, or `null` to leave the auth
 * context's own error. A dismissed passkey prompt reads the passkey sign-in's own leaf, never the browser's English.
 *
 * @param {?AnswerRefusal} err
 * @returns {{messageKey: string, terminal: boolean}|null}
 */
export function mfaRefusalOf(err) {
  if (isPasskeyPromptDismissed(err)) {
    return { messageKey: 'passkey.error.cancelled', terminal: false };
  }
  return typeof err?.messageKey === 'string'
    ? { messageKey: err.messageKey, terminal: err.terminal === true }
    : null;
}
